/***********************************************************************
 * TestRequirementForm.tsx
 *
 * A “smart” form that lets the user **create** or **edit** a single
 * Test-Requirement (incl. CRUD for attachments).  It supports:
 *   • react-hook-form for field/state handling
 *   • dynamic procedure / acceptance-criteria arrays via useFieldArray
 *   • inline attachment management (add / edit / delete / crop)
 *   • conditional UX (create vs edit, loader backdrop, disable rules)
 *
 * Props
 * ────────────────────────────────────────────────────────────────────
 * requirement      – The currently-selected requirement object
 * selectedId       – nodeid of selected requirement (null ⇒ “create” mode)
 * resetTree        – helper from parent to refresh the tree UI
 * setSelectedId    – setter in parent to keep tree + form in sync
 **********************************************************************/

import {
  Box,
  IconButton,
  Grid,
  Chip,
  Backdrop,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useBoundStore } from "Zustand/store/mainStore";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { Button, Checkbox, TextField, Typography } from "Atoms";
import DeletePopUp from "Components/DeletePopUp";
import AttachmentManager from "Atoms/AttachmentManager";
import {
  DOC_EXTS,
  IMAGE_EXTS,
  VIDEO_EXTS,
  getAttachmentChanges,
} from "Utils/helperFunctions";
import { deleteAttachmentsAPI } from "Zustand/services/attachmentService";
import { namespace } from "Zustand/constants";
import { getTestTypesListAPI } from "Zustand/services/projectsService";

interface Attachment {
  id: string;
  title: string;
  description: string;
  file?: File;
  url?: string;
}

interface Parameter {
  param_id?: string;
  name: string;
  value_status: string;
  remark: string;
}
interface RequirementData {
  identifier: string;
  id: string;
  nodeid: string;
  name: string;
  description: string;
  parameters?: Parameter[];
  procedure: string[];
  subcategory?: string;
  category?: string;
  nickname?: string;
  spec?: string;
  specsection?: string;
  testtype?: string;
  testplansection?: string;
  needtis?: boolean;
  duration?: number | string;
  generaldata?: any;
  needpower?: boolean;
  needchamber?: boolean;
  needrack?: boolean;
  manual?: boolean;
  timingcycle?: string;
  timeon?: string;
  timeoff?: string;
  startswith?: string;
  cycles?: number | string;
  soaktime?: string;
  engtime?: string;
  techsetuptime?: number | string;
  techteardowntime?: number | string;
  techmaintenancetime?: number | string;
  technicaltime?: any;
  testrequirementdata?: any;
  acceptancecriteria: string[];
  hasattachment?: Attachment[];
  includeintestplan?: boolean;
}

interface RequirementProps {
  requirement: RequirementData;
  selectedId: string | null;
  resetTree: any;
  setSelectedId: any;
  entityType?: "testplan" | "testspecification";
  parentNodeId?: string;
  onSaved?: (payload: any) => void | Promise<void>;
  saveScope?: "requirement" | "leg";
}

const PARAMETER_TYPE_OPTIONS = [
  { value: "low_temp", label: "Low Temp" },
  { value: "high_temp", label: "High Temp" },
  { value: "low_temp_tol", label: "Low Temp Tolerance" },
  { value: "high_temp_tol", label: "High Temp Tolerance" },
  { value: "ramp_rate", label: "Ramp Rate" },
  { value: "pct_rhh", label: "% RH High" },
  { value: "pct_rhl", label: "% RH Low" },
  { value: "tol_rhh", label: "% RH High Tolerance" },
  { value: "tol_rhl", label: "% RH Low Tolerance" },
  { value: "min_freq", label: "Min Freq" },
  { value: "max_freq", label: "Max Freq" },
  { value: "min_accel", label: "Min Accel" },
  { value: "max_accel", label: "Max Accel" },
  { value: "sweeps", label: "Sweeps" },
  { value: "min_voltage", label: "Min Voltage" },
  { value: "max_voltage", label: "Max Voltage" },
];

const DEFAULT_TESTPLAN_SECTION = "tests";

const TESTPLAN_SECTION_OPTIONS = [
  { value: "parametertestsmall", label: "Parameter test small" },
  { value: "parametertestlarge", label: "Parameter test large" },
  { value: "continuousmonitoring", label: "Continuous monitoring" },
  { value: "visualinspection2", label: "Visual inspection 2" },
  { value: "physicalanalysis", label: "Physical analysis" },
  { value: DEFAULT_TESTPLAN_SECTION, label: "Tests" },
];

const GENERAL_DATA_KEYS = [
  "needtis",
  "needpower",
  "needchamber",
  "needrack",
  "manual",
  "timingcycle",
  "timeon",
  "timeoff",
  "startswith",
  "cycles",
] as const;

const TECHNICAL_TIME_KEYS = [
  "soaktime",
  "engtime",
  "techsetuptime",
  "techteardowntime",
  "techmaintenancetime",
] as const;

const readObjectValue = (source: any, key: string) =>
  source && typeof source === "object" && !Array.isArray(source)
    ? source[key]
    : undefined;

const toSafeString = (value: any) =>
  value === null ||
  value === undefined ||
  (typeof value === "object" && !Array.isArray(value))
    ? ""
    : String(value);

const toSafeNumber = (value: any) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    (typeof value === "object" && !Array.isArray(value))
  ) {
    return 0;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const toSafeBoolean = (value: any) =>
  typeof value === "boolean" ? value : false;

const TestRequirementForm = ({
  requirement,
  selectedId,
  resetTree,
  setSelectedId,
  entityType = "testplan",
  parentNodeId,
  onSaved,
  saveScope = "requirement",
}: RequirementProps) => {
  const {
    createTestRequirement,
    getTestPlanContent,
    getTestSpecificationContent,
    deleteTestRequirement,
    updateTestRequirement,
    uploadAttachments,
    replaceAttachmentFiles,
    loading,
    isUploading,
    setIsUploading,
  } = useBoundStore();

  const location = useLocation();
  const effectiveParentNodeId =
    parentNodeId ||
    (entityType === "testspecification"
      ? location.state?.testspecificationnodeid
      : location.state?.plannodeid);
  const projectCustomer =
    location.state?.project?.testproject?.customer ||
    location.state?.project?.customer ||
    "";

  const [openDialogDelete, setOpenDialogDelete] = useState(false);

  const [originalAttachments, setOriginalAttachments] = useState<Attachment[]>(
    []
  );
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [testTypeOptions, setTestTypeOptions] = useState<
    Array<{ label: string; value: string }>
  >([
    { label: "Vibe", value: "Vibe" },
    { label: "Mechanical", value: "Mechanical" },
    { label: "Electrical", value: "Electrical" },
  ]);

  const {
    register,
    control,
    handleSubmit,
    formState: { dirtyFields, isDirty },
    reset,
    watch,
  } = useForm<RequirementData>();

  const {
    fields: parameterFields,
    append: appendParameter,
    remove: removeParameter,
    replace: replaceParameters,
  } = useFieldArray<RequirementData, "parameters", "id">({
    control,
    name: "parameters",
  });

  const procedureFieldArray: any = useFieldArray<any>({
    control,
    name: "procedure",
  });

  const acceptanceFieldArray = useFieldArray<any>({
    control,
    name: "acceptancecriteria",
  });

  const procedureFields = procedureFieldArray.fields;
  const appendProcedure = procedureFieldArray.append;
  const removeProcedure = procedureFieldArray.remove;

  const acceptanceFields = acceptanceFieldArray.fields;
  const appendAcceptance = acceptanceFieldArray.append;
  const removeAcceptance = acceptanceFieldArray.remove;

  let isEditMode = selectedId !== null;
  const isLegScopedSave = saveScope === "leg";

  const onSubmit = async (data: RequirementData) => {
    let testRequirementIdentifier = uuidv4();

    const payload: any = {
      lang: "en",
      nodeid: isEditMode
        ? requirement.nodeid
        : namespace + "/" + testRequirementIdentifier,
      identifier: testRequirementIdentifier,
      // sourcereference: ["","","",""],
    };

    payload.istestrequirementofnodeid = effectiveParentNodeId;

    if (dirtyFields.includeintestplan) {
      payload.includeintestplan = data.includeintestplan;
    }

    payload.nickname = toSafeString(data.nickname);
    payload.duration = toSafeNumber(data.duration);

    // Flat fields
    (
      [
        "name",
        "description",
        "category",
        "subcategory",
        "spec",
        "specsection",
        "testtype",
        "testplansection",
        "includeintestplan",
      ] as const
    ).forEach((key) => {
      if (dirtyFields[key]) {
        payload[key] = data[key];
      }
    });

    const hasDirtyGeneralData = GENERAL_DATA_KEYS.some(
      (key) => (dirtyFields as any)[key]
    );
    if (hasDirtyGeneralData) {
      payload.generaldata = GENERAL_DATA_KEYS.reduce((acc, key) => {
        acc[key] = data[key];
        return acc;
      }, {} as Record<string, any>);
    }

    const hasDirtyTechnicalTime = TECHNICAL_TIME_KEYS.some(
      (key) => (dirtyFields as any)[key]
    );
    if (hasDirtyTechnicalTime) {
      payload.technicaltime = TECHNICAL_TIME_KEYS.reduce((acc, key) => {
        acc[key] = data[key];
        return acc;
      }, {} as Record<string, any>);
    }

    const originalParams = coerceParameters(requirement?.parameters);
    const currentParams = coerceParameters(data.parameters);
    const paramsChanged =
      JSON.stringify(originalParams) !== JSON.stringify(currentParams);

    if (paramsChanged) {
      payload.parameters = currentParams;
    }

    // procedure array
    if (dirtyFields.procedure?.length) {
      const changedProcedures = data.procedure.filter((item, index) => {
        return dirtyFields.procedure?.[index];
      });
      if (
        changedProcedures?.length > 0 ||
        data?.procedure?.length !== requirement?.procedure.length
      ) {
        payload.procedure = data?.procedure;
      }
    }

    // acceptancecriteria array
    if (dirtyFields.acceptancecriteria) {
      const changedAC = data.acceptancecriteria.filter((item, index) => {
        return dirtyFields.acceptancecriteria?.[index];
      });
      if (
        changedAC?.length > 0 ||
        data?.acceptancecriteria?.length !==
          requirement?.acceptancecriteria?.length
      ) {
        payload.acceptancecriteria = data?.acceptancecriteria;
      }
    }

    if (isLegScopedSave) {
      await onSaved?.(payload);
      reset();
      setAttachments([]);
      setOriginalAttachments([]);
      setSelectedId(null);
      return;
    }

    const { toAdd, toDelete, toEdit, toReplace } = getAttachmentChanges(
      originalAttachments,
      attachments
    );

    if (!isEditMode) {
      await createTestRequirement(payload); // POST

      // Handle attachments
      if (toAdd.length > 0) {
        setIsUploading(true);
        await uploadAttachments(payload.nodeid, [...toAdd], []);
        setIsUploading(false);
      }
    } else {
      if (isDirty) {
        await updateTestRequirement(payload); // PUT
      }

      // Handle attachments

      if (toAdd.length > 0) {
        setIsUploading(true);
        await uploadAttachments(payload.nodeid, [...toAdd], []);
        setIsUploading(false);
      }

      if (toDelete.length > 0) {
        setIsUploading(true);
        await deleteAttachmentsAPI(toDelete.map((item: any) => item.nodeid));
        setIsUploading(false);
      }
      if (toEdit.length > 0) {
        setIsUploading(true);
        await uploadAttachments(payload.nodeid, [], [...toEdit]);
        setIsUploading(false);
      }

      // Cropped remote images: overwrite the existing file by fileid.
      if (toReplace.length > 0) {
        setIsUploading(true);
        await replaceAttachmentFiles(toReplace);
        setIsUploading(false);
      }
    }

    // Reset form and attachments

    if (onSaved) {
      await onSaved(payload);
    } else if (entityType === "testspecification") {
      await getTestSpecificationContent(effectiveParentNodeId);
    } else {
      await getTestPlanContent(effectiveParentNodeId);
    }
    reset();
    setAttachments([]);
    setOriginalAttachments([]);
    setSelectedId(null);

    setSelectedId(null);

    setTimeout(() => {
      setSelectedId(payload.nodeid);
    }, 1000);
  };

  function coerceParameters(input: any): Parameter[] {
    if (Array.isArray(input)) {
      // ensure shape
      return input.map((p: any) => ({
        param_id: toSafeString(p?.param_id),
        name: toSafeString(p?.name),
        value_status: toSafeString(p?.value_status),
        remark: toSafeString(p?.remark),
      }));
    }
    // legacy string/null -> empty array
    return [];
  }

  const handleDelete = async () => {
    setOpenDialogDelete(false);
    if (selectedId !== null) {
      await deleteTestRequirement(selectedId);
      if (entityType === "testspecification") {
        await getTestSpecificationContent(effectiveParentNodeId);
      } else {
        await getTestPlanContent(effectiveParentNodeId);
      }
      resetTree();
    }
  };

  const handleDeleteDialogToggle = () => {
    setOpenDialogDelete(!openDialogDelete);
  };

  const normalizeApiAttachments = (apiData: any[]): Attachment[] => {
    return apiData.map((item) => ({
      id: item.attachment.file.fileid,
      title: item.attachment.title,
      description: item.attachment.description,
      url: item.attachment.file.url,
      nodeid: item.attachment.nodeid,
      mimetype: item.attachment.file.mimetype,
    }));
  };

  const normalizeTestTypeOptions = (response: any) => {
    const source =
      (Array.isArray(response) && response) ||
      response?.testtypes ||
      response?.testTypes ||
      response?.testtypeslist ||
      response?.data ||
      [];

    if (!Array.isArray(source)) return [];

    return source
      .map((item: any) => {
        const value =
          typeof item === "string"
            ? item
            : item?.name || item?.testtype || item?.type || item?.id || "";
        return value ? { label: String(value), value: String(value) } : null;
      })
      .filter(Boolean) as Array<{ label: string; value: string }>;
  };

  useEffect(() => {
    const loadTestTypes = async () => {
      try {
        const response = await getTestTypesListAPI();
        const options = normalizeTestTypeOptions(response);
        if (options.length > 0) {
          setTestTypeOptions(options);
        }
      } catch (error) {
        console.error("Failed to load test types", error);
      }
    };

    loadTestTypes();
  }, []);

  // Call this when the user clicks the “View in PDF” button for a requirement
  // const openPdfAt = (url: string, page: number) => {
  //   setPdfUrl(url);
  //   setPdfPage(page);       // 1-based page number
  //   setPdfOpen(true);       // keep it open across clicks; props will update
  // };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    // if user selects any requirement from tree then mapping data to form
    setAttachments([]);
    if (requirement) {
      const requirementData =
        requirement?.testrequirementdata &&
        typeof requirement.testrequirementdata === "object" &&
        !Array.isArray(requirement.testrequirementdata)
          ? {
              ...requirement,
              ...requirement.testrequirementdata,
              nodeid: requirement.nodeid,
            }
          : requirement;
      const generalData = requirementData?.generaldata ?? {};
      const technicalTime = requirementData?.technicaltime ?? {};

      reset({
        id: toSafeString(requirementData?.id),
        nodeid: toSafeString(requirementData?.nodeid),
        name: toSafeString(requirementData?.name),
        description: toSafeString(requirementData?.description),
        parameters: coerceParameters(requirementData.parameters),
        procedure: requirementData?.procedure ?? [],
        subcategory: toSafeString(requirementData?.subcategory),
        category: toSafeString(requirementData?.category),
        nickname: toSafeString(requirementData?.nickname),
        spec: toSafeString(requirementData?.spec),
        specsection: toSafeString(requirementData?.specsection),
        testtype: toSafeString(requirementData?.testtype),
        testplansection:
          toSafeString(requirementData?.testplansection) ||
          DEFAULT_TESTPLAN_SECTION,
        needtis: toSafeBoolean(
          readObjectValue(generalData, "needtis") ?? requirementData?.needtis
        ),
        duration: toSafeNumber(requirementData?.duration),
        needpower: toSafeBoolean(
          readObjectValue(generalData, "needpower") ?? requirementData?.needpower
        ),
        needchamber: toSafeBoolean(
          readObjectValue(generalData, "needchamber") ??
            requirementData?.needchamber
        ),
        needrack: toSafeBoolean(
          readObjectValue(generalData, "needrack") ?? requirementData?.needrack
        ),
        manual: toSafeBoolean(
          readObjectValue(generalData, "manual") ?? requirementData?.manual
        ),
        timingcycle: toSafeString(
          readObjectValue(generalData, "timingcycle") ??
            requirementData?.timingcycle
        ),
        timeon: toSafeString(
          readObjectValue(generalData, "timeon") ?? requirementData?.timeon
        ),
        timeoff: toSafeString(
          readObjectValue(generalData, "timeoff") ?? requirementData?.timeoff
        ),
        startswith: toSafeString(
          readObjectValue(generalData, "startswith") ??
            requirementData?.startswith
        ),
        cycles: toSafeNumber(
          readObjectValue(generalData, "cycles") ?? requirementData?.cycles
        ),
        soaktime: toSafeString(
          readObjectValue(technicalTime, "soaktime") ?? requirementData?.soaktime
        ),
        engtime: toSafeString(
          readObjectValue(technicalTime, "engtime") ?? requirementData?.engtime
        ),
        techsetuptime: toSafeNumber(
          readObjectValue(technicalTime, "techsetuptime") ??
            requirementData?.techsetuptime
        ),
        techteardowntime: toSafeNumber(
          readObjectValue(technicalTime, "techteardowntime") ??
            requirementData?.techteardowntime
        ),
        techmaintenancetime: toSafeNumber(
          readObjectValue(technicalTime, "techmaintenancetime") ??
            requirementData?.techmaintenancetime
        ),
        acceptancecriteria: requirementData?.acceptancecriteria ?? [],
        hasattachment: requirementData?.hasattachment ?? [],
        includeintestplan: requirementData?.includeintestplan ?? false,
      });

      const normalized = normalizeApiAttachments(
        requirementData?.hasattachment || []
      );
      setOriginalAttachments(normalized);
      setAttachments(normalized); // passed to AttachmentManager
    }
  }, [requirement]);

  const attachmentsChanged = useMemo(() => {
    return JSON.stringify(originalAttachments) !== JSON.stringify(attachments);
  }, [attachments, originalAttachments]);

  
  // reset form fields when switching between create/edit mode
  useEffect(() => {
    if (!isEditMode) {
      reset({
        id: "",
        nodeid: "",
        name: "",
        description: "",
        parameters: [],
        procedure: [],
        subcategory: "",
        category: "",
        nickname: "",
        spec: "",
        specsection: "",
        testtype: "",
        testplansection: DEFAULT_TESTPLAN_SECTION,
        needtis: false,
        duration: "",
        needpower: false,
        needchamber: false,
        needrack: false,
        manual: false,
        timingcycle: "",
        timeon: "",
        timeoff: "",
        startswith: "",
        cycles: 0,
        soaktime: "",
        engtime: "",
        techsetuptime: 0,
        techteardowntime: 0,
        techmaintenancetime: 0,
        acceptancecriteria: [],
        hasattachment: [],
        includeintestplan: false,
      });
      replaceParameters([]);
      procedureFieldArray.replace([]);
      acceptanceFieldArray.replace([]);
      setAttachments([]);
      setOriginalAttachments([]);
    }
  }, [isEditMode]);

  const durationValue = watch("duration");
  const isDurationInvalid =
    !String(durationValue ?? "").trim() ||
    !Number.isInteger(Number(durationValue)) ||
    Number(durationValue) <= 0;

  return (
    <>
      <Backdrop
        open={loading}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 1,
          backdropFilter: "blur(4px)",
          flexDirection: "column",
          opacity: 0,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ mt: 2 }} color={"#FFFFFF"}>
          Bitte warten...
        </Typography>
      </Backdrop>

      <DeletePopUp
        open={openDialogDelete}
        handleClose={handleDeleteDialogToggle}
        handleDelete={handleDelete}
      ></DeletePopUp>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                mb: 1,
              }}
            >
              <Box sx={{ flex: "0 0 auto" }}>
                {/* RHF Controller lets us bind the boolean easily */}
                <Controller
                  control={control}
                  name="includeintestplan"
                  defaultValue={false}
                  render={({ field }) => (
                    <Checkbox
                      label="Active"
                      checked={field.value || false}
                      onChange={field.onChange} // sends boolean straight to RHF
                      value={watch("includeintestplan") || false}
                    />
                  )}
                />
              </Box>

              <Box
                sx={{
                  flex: "1 1 auto",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ minWidth: 260 }}>
                  <Controller
                    control={control}
                    name="testplansection"
                    defaultValue={DEFAULT_TESTPLAN_SECTION}
                    render={({ field }) => (
                      <FormControl fullWidth size="small">
                        <InputLabel>Test Plan Section</InputLabel>
                        <Select
                          label="Test Plan Section"
                          value={toSafeString(field.value) || DEFAULT_TESTPLAN_SECTION}
                          onChange={field.onChange}
                        >
                          {TESTPLAN_SECTION_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 2, flex: "0 0 auto" }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={
                    !watch("name")?.trim() ||
                    (isEditMode &&
                      !isDirty &&
                      (!attachmentsChanged || isLegScopedSave)) || // inEditMode if there is any change in attachments
                    isUploading
                  }
                >
                  {isUploading ? (
                    <CircularProgress
                      size={24}
                      color="inherit"
                      sx={{ marginRight: 1 }}
                    />
                  ) : (
                    ""
                  )}

                  {!isEditMode ? "Create" : "Save"}
                </Button>
                {isEditMode && !isLegScopedSave && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDeleteDialogToggle}
                  >
                    Delete
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom>
              <b>General Data</b>
            </Typography>
          </Grid>

          <Grid size={6}>
            <TextField
              label="Name"
              fullWidth
              {...register("name", { required: true })}
              error={!watch("name")?.trim()}
              value={toSafeString(watch("name"))}
            />
          </Grid>

          {/* Category/Sub Category are currently hidden from create/edit mode.
          The backend data is still preserved when editing existing requirements. */}
          {/* <Grid size={6}>
            <TextField
              label={isEditMode ? "Category" : "Sub Category"}
              fullWidth
              {...register("subcategory", { required: true })}
              error={!watch("subcategory")?.trim()}
              value={toSafeString(watch("subcategory"))}
            />
          </Grid>

          {!isEditMode && (
            <Grid size={6}>
              <TextField
                label="Category"
                fullWidth
                {...register("category", { required: true })}
                error={!watch("category")?.trim()}
                value={toSafeString(watch("category"))}
              />
            </Grid>
          )} */}

          <Grid size={6}>
            <TextField
              label="Nick Name"
              fullWidth
              required
              inputProps={{ maxLength: 255 }}
              {...register("nickname", { required: true })}
              error={!String(watch("nickname") ?? "").trim()}
              value={toSafeString(watch("nickname"))}
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label="Spec"
              fullWidth
              required
              inputProps={{ maxLength: 255 }}
              {...register("spec", { required: true })}
              error={!String(watch("spec") ?? "").trim()}
              value={toSafeString(watch("spec"))}
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label="Spec Section"
              fullWidth
              inputProps={{ maxLength: 255 }}
              {...register("specsection")}
              value={toSafeString(watch("specsection"))}
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label="Customer"
              fullWidth
              required
              disabled
              value={projectCustomer}
            />
          </Grid>

          <Grid size={6}>
            <Controller
              control={control}
              name="testtype"
              defaultValue=""
              rules={{ required: true }}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  margin="normal"
                  required
                  error={!String(watch("testtype") ?? "").trim()}
                >
                  <InputLabel>Test Type</InputLabel>
                  <Select
                    label="Test Type"
                    value={toSafeString(field.value)}
                    onChange={field.onChange}
                  >
                    {testTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label="Duration (Hrs)"
              type="number"
              fullWidth
              required
              inputProps={{ step: 1, min: 1 }}
              {...register("duration", {
                required: true,
                setValueAs: (value) => (value === "" ? "" : Number(value)),
                validate: (value) => {
                  const numericValue = Number(value);
                  return (
                    (Number.isInteger(numericValue) && numericValue > 0) ||
                    "Duration must be a whole number greater than 0"
                  );
                },
              })}
              error={isDurationInvalid}
              helperText={
                isDurationInvalid
                  ? "Duration must be a whole number greater than 0"
                  : ""
              }
              value={watch("duration") ?? ""}
            />
          </Grid>

          <Grid size={6}>
            <Controller
              control={control}
              name="needtis"
              defaultValue={false}
              render={({ field }) => (
                <Checkbox
                  label="Need TIS?"
                  checked={field.value || false}
                  onChange={field.onChange}
                  value={watch("needtis") || false}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              label="Purpose"
              fullWidth
              multiline
              rows={3}
              {...register("description")}
              value={toSafeString(watch("description"))}
            />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
              <b>Test Setup</b>
            </Typography>
          </Grid>

          <Grid size={6}>
            <Controller
              control={control}
              name="needpower"
              defaultValue={false}
              render={({ field }) => (
                <Checkbox
                  label="Powered"
                  checked={field.value || false}
                  onChange={field.onChange}
                  value={watch("needpower") || false}
                />
              )}
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label="Time On"
              fullWidth
              inputProps={{ maxLength: 255 }}
              {...register("timeon")}
              value={toSafeString(watch("timeon"))}
            />
          </Grid>

          <Grid size={6}>
            <Controller
              control={control}
              name="needchamber"
              defaultValue={false}
              render={({ field }) => (
                <Checkbox
                  label="Need Chamber?"
                  checked={field.value || false}
                  onChange={field.onChange}
                  value={watch("needchamber") || false}
                />
              )}
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label="Time Off"
              fullWidth
              inputProps={{ maxLength: 255 }}
              {...register("timeoff")}
              value={toSafeString(watch("timeoff"))}
            />
          </Grid>

          <Grid size={6}>
            <Controller
              control={control}
              name="needrack"
              defaultValue={false}
              render={({ field }) => (
                <Checkbox
                  label="Need Hil/Rack?"
                  checked={field.value || false}
                  onChange={field.onChange}
                  value={watch("needrack") || false}
                />
              )}
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label="Starts With"
              fullWidth
              inputProps={{ maxLength: 255 }}
              {...register("startswith")}
              value={toSafeString(watch("startswith"))}
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label="Timing Cycle"
              fullWidth
              inputProps={{ maxLength: 255 }}
              {...register("timingcycle")}
              value={toSafeString(watch("timingcycle"))}
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label="Qty Cycles"
              type="number"
              fullWidth
              {...register("cycles", {
                setValueAs: (value) => (value === "" ? "" : Number(value)),
              })}
              value={toSafeNumber(watch("cycles"))}
            />
          </Grid>

          <Grid size={6}>
            <Controller
              control={control}
              name="manual"
              defaultValue={false}
              render={({ field }) => (
                <Checkbox
                  label="Manual"
                  checked={field.value || false}
                  onChange={field.onChange}
                  value={watch("manual") || false}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom>
              <b>Parameters</b>{" "}
              <Chip
                label={<b>{parameterFields.length}</b>}
                color="primary"
                size="small"
              />
            </Typography>

            <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
              {parameterFields.map((field, idx) => (
                <Box
                  key={field.id}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  sx={{ mb: 1 }}
                >
                  <TextField
                    label="Name"
                    fullWidth
                    {...register(`parameters.${idx}.name` as const)}
                    value={toSafeString(watch(`parameters.${idx}.name`))}
                  />
                  <TextField
                    label="Value / Status"
                    fullWidth
                    {...register(`parameters.${idx}.value_status` as const)}
                    value={toSafeString(
                      watch(`parameters.${idx}.value_status`)
                    )}
                  />
                  <TextField
                    label="Remark"
                    fullWidth
                    {...register(`parameters.${idx}.remark` as const)}
                    value={toSafeString(watch(`parameters.${idx}.remark`))}
                  />
                  <Controller
                    control={control}
                    name={`parameters.${idx}.param_id` as const}
                    defaultValue=""
                    render={({ field: typeField }) => (
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Type</InputLabel>
                        <Select
                          label="Type"
                          value={toSafeString(typeField.value)}
                          onChange={typeField.onChange}
                        >
                          {PARAMETER_TYPE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  <IconButton onClick={() => removeParameter(idx)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </Box>
              ))}
            </Box>

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() =>
                appendParameter({
                  param_id: "",
                  name: "",
                  value_status: "",
                  remark: "",
                })
              }
              sx={{ mt: 1 }}
            >
              Add Parameter
            </Button>
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom>
              <b>Test Method Description</b>{" "}
              <Chip
                label={<b>{procedureFields.length}</b>}
                color="primary"
                size="small"
              />
            </Typography>
            <Box sx={{ maxHeight: 130, overflowY: "auto" }}>
              {procedureFields.map((field: any, idx: any) => (
                <Box key={field.id} display="flex" alignItems="center">
                  <TextField
                    multiline
                    fullWidth
                    label={`Step ${idx + 1}`}
                    {...register(`procedure.${idx}` as const)}
                  />
                  <IconButton
                    onClick={() => removeProcedure(idx)}
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon color="error" />
                  </IconButton>
                </Box>
              ))}
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => appendProcedure("")}
              sx={{ mt: 1 }}
            >
              Add Step
            </Button>
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
              <b>Technical Time</b>
            </Typography>
          </Grid>

          <Grid size={6}>
            <TextField
              label="Soak Time"
              fullWidth
              inputProps={{ maxLength: 255 }}
              {...register("soaktime")}
              value={toSafeString(watch("soaktime"))}
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label="Tech Setup Time (mins)"
              type="number"
              fullWidth
              {...register("techsetuptime", {
                setValueAs: (value) => (value === "" ? "" : Number(value)),
              })}
              value={toSafeNumber(watch("techsetuptime"))}
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label="Engineering Time (mins)"
              fullWidth
              inputProps={{ maxLength: 255 }}
              {...register("engtime")}
              value={toSafeString(watch("engtime"))}
            />
          </Grid>

          <Grid size={6}>
            <TextField
              label="Tech Teardown Time (mins)"
              type="number"
              fullWidth
              {...register("techteardowntime", {
                setValueAs: (value) => (value === "" ? "" : Number(value)),
              })}
              value={toSafeNumber(watch("techteardowntime"))}
            />
          </Grid>

          <Grid size={6}></Grid>

          <Grid size={6}>
            <TextField
              label="Tech Maintenance Time (mins)"
              type="number"
              fullWidth
              {...register("techmaintenancetime", {
                setValueAs: (value) => (value === "" ? "" : Number(value)),
              })}
              value={toSafeNumber(watch("techmaintenancetime"))}
            />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom>
              <b>Acceptance Criteria</b>{" "}
              <Chip
                label={<b>{acceptanceFields.length}</b>}
                color="primary"
                size="small"
              />
            </Typography>
            <Box sx={{ maxHeight: 130, overflowY: "auto" }}>
              {acceptanceFields.map((field, idx) => (
                <Box key={field.id} display="flex" alignItems="center">
                  <TextField
                    fullWidth
                    label={`Criteria ${idx + 1}`}
                    {...register(`acceptancecriteria.${idx}` as const)}
                  />
                  <IconButton
                    onClick={() => removeAcceptance(idx)}
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon color="error" />
                  </IconButton>
                </Box>
              ))}
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => appendAcceptance("")}
              sx={{ mt: 1 }}
            >
              Add Criteria
            </Button>

            <br />
            <br />

            {!isLegScopedSave && (
              <AttachmentManager
                _attachments={attachments}
                onChange={setAttachments}
                // disableFileChangeForExisting={true}
                defaultExpanded={false}
                allowedFileExtensions={[
                  ...IMAGE_EXTS,
                  ...VIDEO_EXTS,
                  ...DOC_EXTS,
                ]}
                cropFeature={true}
                previewImages={true}
                multiple
              />
            )}
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default TestRequirementForm;
